import { useEffect, useSyncExternalStore } from "react";
import {
  skipNextSpin,
  skippableSpinCount,
  subscribeToSkippableSpins,
} from "../Games/Shared/spinSkipRegistry";

/**
 * Slam stop for spins in flight — GDD §13.
 *
 * ── One press, one spin ──
 *
 * A Golden Mystery board can have thirty cells spinning at once, stopping
 * 750ms apart, which is nine seconds of feature. Collapsing all of it on a
 * single press would throw away the whole reveal — the player wants it
 * FASTER, not gone. So each press brings down exactly one cell, and spamming
 * walks the cascade as quickly as they can tap.
 *
 * The registry always hands back the lowest stop order still waiting, so the
 * board arrives in the order it was always going to arrive in. Nothing is
 * reordered and nothing lands anywhere it would not have landed.
 *
 * ── Why a global listener and not ButtonPlay ──
 *
 * These sequences run under `criticalAnimationPlaying`, the universal input
 * blocker: ButtonPlay bails out on it in five separate handlers before it ever
 * resolves an action. Threading an exception through all five would put the
 * button's state machine — turbo holds, queued stops, autoplay gating — at
 * risk for a feature that needs none of it.
 *
 * So the skip listens for itself, and ONLY while there is something to skip.
 * With nothing spinning, nothing is attached at all, so this cannot affect any
 * other input in the game. Inside the window a tap anywhere and the spacebar
 * both work, and a press on the Spin button works too — it lands on the canvas
 * like any other tap, while the button's own handler still refuses it.
 *
 * The event is deliberately NOT swallowed. Nothing else acts on input during
 * this window — that is what criticalAnimationPlaying guarantees — so there is
 * nothing to protect against, and stopping propagation from a window listener
 * is how an unrelated handler quietly breaks months later.
 */
// ── ISOLATION SWITCH ──
//
// Flip to false to take the whole skip feature out of the running: no
// listeners, no registry reads, nothing subscribed. The spins themselves still
// register — that is per-controller and harmless — but nothing can act on it.
//
// Here to answer one question quickly when something else misbehaves: is the
// skip involved at all? If a bug survives this being false, it is not this
// feature's, and the search moves on.
const SKIP_ENABLED = true;

// ── THE FLOOR UNDER THE CASCADE ──
//
// A press cuts a spin's WAIT and nothing else — the deceleration and the
// landing bounce after it always play in full, and neither is skippable. But
// nothing sat between two PRESSES, so two taps 30ms apart started two landings
// 30ms apart and the board arrived in a heap.
//
// Nothing broke: the cells are independent, the shared spin sound is
// ref-counted, and the runner gathers every cell with Promise.all. It read as
// fragile, which is its own problem.
//
// This is the base game's MIN_ACTION_GAP, in the same place ButtonPlay keeps
// it — on the INPUT, not in the state machine. The registry stays a pure
// ordered list with no clock in it, which is why its tests can still drive it
// synchronously.
//
// ── Tuning ──
//
// One number. Lower is snappier, higher gives each landing more room. The
// cascade stops cells 750ms apart on its own, so 200ms still lets a player
// clear a thirty-cell board about four times faster than waiting it out.
const MIN_SKIP_GAP_MS = 200;

const useRevealSkip = () => {
  // Straight off the registry rather than a mirrored flag in Redux: the list
  // of what is waiting IS the truth, so the two can never drift.
  const count = useSyncExternalStore(
    subscribeToSkippableSpins,
    skippableSpinCount,
    skippableSpinCount,
  );

  // Only whether the window is OPEN matters, never how many are left in it.
  // Depending on the count itself would tear the listeners down and rebuild
  // them on each of thirty stops, which is how a press lands in the gap
  // between them and is lost.
  const windowOpen = count > 0;

  useEffect(() => {
    if (!SKIP_ENABLED || !windowOpen) return undefined;

    // Shared by both inputs, so tapping and spacebarring alternately cannot
    // beat the floor either. Only a press that actually TOOK something starts
    // the next window — a spacebar that passed over a pointerOnly entry and
    // found nothing must not lock out the click that would have worked.
    let lastSkipAt = -Infinity;
    const takeOne = (source) => {
      const now = performance.now();
      if (now - lastSkipAt < MIN_SKIP_GAP_MS) return;
      if (skipNextSpin(source)) lastSkipAt = now;
    };

    const onPointerDown = () => {
      takeOne("pointer");
    };

    const onKeyDown = (event) => {
      // A HELD key must not drain the board. Spamming is the player choosing
      // to go faster; leaning on the spacebar is not.
      if (event.repeat) return;
      const isSpace =
        event.code === "Space" || event.key === " " || event.keyCode === 32;
      if (!isSpace) return;
      // Space scrolls the page when the canvas does not hold focus.
      event.preventDefault();
      // Some spins refuse the keyboard — the gamble conveyor, because the
      // spacebar is what STARTED it. See registerSkippableSpin's pointerOnly.
      takeOne("key");
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [windowOpen]);
};

export default useRevealSkip;
